// Pure back-navigation decision for the PV1/PV2 verification page. Framework-free: no Vue,
// no router. PrescriptionVerificationDetailsPage executes the returned destination.

export type VerificationBackDestination =
    | { kind: 'orderVerification'; orderId: number }
    | { kind: 'pendingDispenseErrors' }
    | { kind: 'pv1Queue' }
    | { kind: 'previous' }

export interface VerificationBackInput {
    /** The caller asked to return to the order (e.g. after verifying an order's prescription). */
    backToOrderPage: boolean
    /** Order id from the route params; 0 when the page was not opened from an order. */
    routeOrderId: number
    /** Id of the order loaded for this prescription, if any. */
    loadedOrderId: number | null | undefined
    /** 'pv1' | 'pv2' | 'audit' | '' */
    pageMode: string
    /** Tenant configuration: SkipOrderVerificationAfterPV1. */
    skipOrderVerificationAfterPV1: boolean
    hasDispenseErrorRoute: boolean
    /** Opened from the PV1 queue in batch mode (?batch). */
    isBatch: boolean
}

/**
 * Where leaving the verification page lands. Priority (pinned by spec):
 *   order verification (unless skipped after PV1) → pending dispense errors → PV1 queue (batch)
 *   → previous route.
 * The skip applies only on PV1; PV2 always returns to the order when one is in play.
 */
export function decideVerificationBackDestination(input: VerificationBackInput): VerificationBackDestination {
    const orderId = input.routeOrderId || input.loadedOrderId
    const skipOrderPage = input.skipOrderVerificationAfterPV1 && input.pageMode === 'pv1'

    if ((input.backToOrderPage || input.routeOrderId) && orderId && !skipOrderPage) {
        return { kind: 'orderVerification', orderId }
    }
    if (input.hasDispenseErrorRoute) return { kind: 'pendingDispenseErrors' }
    if (input.isBatch) return { kind: 'pv1Queue' }
    return { kind: 'previous' }
}
